import { useCallback, useEffect, useRef, useState } from "react";
import { IoIosArrowDown } from "react-icons/io";
import LoaderSpiner from "../LoaderSpiner";
import styles from "./PhoneNumberList.module.css";

const SECRET_KEY = "rajat";

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timeoutRef = useRef(null);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timeoutRef.current);
  }, [value, delay]);

  return debouncedValue;
};

const PhoneNumberList = ({ onNumberSelect, mode }) => {
  const [numbers, setNumbers] = useState([]);
  const [assignedNumbers, setAssignedNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCountryName, setSelectedCountryName] =
    useState("Select a country");
  const [selectedCountryFlag, setSelectedCountryFlag] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [callType, setCallType] = useState("Local");
  const [locationFilter, setLocationFilter] = useState("");
  const [isBuying, setIsBuying] = useState(false);
  const limit = 5;

  // Debounce the location filter input
  const debouncedLocationFilter = useDebounce(locationFilter, 300);

  const countries = [
    {
      code: "IN",
      name: "India",
      flag: "https://imgs.search.brave.com/OlmC3jl1zktWrR9NHftYPYXCHpiu3F6rkCLsmwT5SF8/rs:fit:860:0:0:0/g:ce/aHR0cHM6Ly9pbWcu/ZnJlZXBpay5jb20v/cHJlbWl1bS1waG90/by9hcnQtaW5kaWEt/aW5kZXBlbmRlbmNl/LWRheS13aXRoLWZs/YWdfMzk1NTE0LTMy/OS5qcGc_c2VtdD1h/aXNfaHlicmlk",
    },
    {
      code: "US",
      name: "United States",
      flag: "https://imgs.search.brave.com/A1UXS5EDFvrzmLSGgxxdyTcvZNbIWGzUwBziVeM77Dg/rs:fit:860:0:0:0/g:ce/aHR0cHM6Ly90NC5m/dGNkbi5uZXQvanBn/LzAwLzY1LzEyLzQ5/LzM2MF9GXzY1MTI0/OTA4X1VwTkh6VGRR/bjJITkFJVDh5WXF5/Yk1RZUdmc1JtVmM3/LmpwZw",
    },
    {
      code: "GB",
      name: "United Kingdom",
      flag: "https://imgs.search.brave.com/UlRGi8d3fV9-sJutG8jCE1l-WAuA5svmpFLqeRtunhA/rs:fit:860:0:0:0/g:ce/aHR0cHM6Ly90NC5m/dGNkbi5uZXQvanBn/LzAxLzEyLzQ2Lzc1/LzM2MF9GXzExMjQ2/NzUzNF85amFDWlpS/QUVNc0ljSUNjNUF6/ME5TWDdSZW5JQUt3/OS5qcGc",
    },
    {
      code: "CA",
      name: "Canada",
      flag: "https://imgs.search.brave.com/kWI34R-2jBwSqBBvWmye-982NrTIdG7xOW_F7eUNfHk/rs:fit:860:0:0:0/g:ce/aHR0cHM6Ly93d3cu/Y29sb25pYWxmbGFn/LmNvbS9jZG4vc2hv/cC9wcm9kdWN0cy9j/YW5hZGEtZmxhZ19f/MzIzNTMuMTYzOTY5/MDM2My4xMjgwLjEy/ODAuanBnP3Y9MTY3/MzM4OTY5NSZ3aWR0/aD0xMjgw",
    },
  ];

  const handleCountrySelect = (country) => {
    setSelectedCountry(country.code);
    setSelectedCountryName(country.name);
    setSelectedCountryFlag(country.flag);
    setIsDropdownOpen(false);
    setOffset(0);
    setLocationFilter("");
  };

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const handleCallTypeChange = (e) => {
    setCallType(e.target.value);
  };

  const handleLocationFilterChange = (e) => {
    setLocationFilter(e.target.value);
  };

  const fetchNumbers = useCallback(
    async (country, newOffset) => {
      if (!country) return;
      setLoading(true);
      try {
        const baseUrl =
          "https://prod.toxboxai.com";
        const apiUrl =
          country === "IN"
            ? `${baseUrl}/api/plivo/search`
            : `${baseUrl}/api/twilio/numbers`;
        const response = await fetch(
          `${apiUrl}?country_iso=${country}&type=${callType.toLowerCase()}&limit=${limit}&offset=${newOffset}`
        );
        const data = await response.json();

        if (country === "IN") {
          const mappedNumbers = (data.data.objects || []).map((num) => ({
            number: num.number.replace("+", ""),
            city: num.city || "",
            region: num.region || "",
            voice_enabled: num.voice_enabled || true,
            sms_enabled: num.sms_enabled || false,
            mms_enabled: num.mms_enabled || false,
            monthly_rental_rate: num.monthly_rental_rate || "N/A",
            status: "available",
          }));
          setNumbers(mappedNumbers);
          setTotalCount(data.data.meta?.total_count || 0);
        } else {
          const mappedNumbers = (data || []).map((num) => ({
            number: num.phone_number.replace("+", ""),
            city: num.locality || "",
            region: num.region || "",
            voice_enabled: num.capabilities.voice,
            sms_enabled: num.capabilities.SMS,
            mms_enabled: num.capabilities.MMS,
            monthly_rental_rate: "N/A",
            status: "available",
          }));
          setNumbers(mappedNumbers);
          setTotalCount(data.length);
        }
      } catch (error) {
        console.error("Error fetching numbers:", error);
      } finally {
        setLoading(false);
      }
    },
    [callType]
  );

  const fetchAssignedNumbers = useCallback(async () => {
    if (mode !== "inbound") {
      setAssignedNumbers([]);
      return;
    }
    try {
      const response = await fetch(
        "https://prod.toxboxai.com/api/numbers",
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to fetch assigned numbers");
      const inboundNumbers = data
        .filter((num) => num.type === "inbound")
        .map((num) => ({
          number: num.phoneNumber.replace("+", ""),
          city: "",
          region: "",
          voice_enabled: true,
          sms_enabled: false,
          mms_enabled: false,
          monthly_rental_rate: "N/A",
          status: "assigned",
        }));
      setAssignedNumbers(inboundNumbers);
    } catch (error) {
      console.error("Error fetching assigned numbers:", error);
    }
  }, [mode]);

  useEffect(() => {
    fetchNumbers(selectedCountry, offset);
    fetchAssignedNumbers();
  }, [selectedCountry, offset, fetchNumbers, fetchAssignedNumbers]);

  // Filter available numbers by location
  const filteredNumbers = numbers.filter((num) => {
    const location = `${num.city ? `${num.city}, ` : ""}${num.region
      }`.toLowerCase();
    return debouncedLocationFilter
      ? location.includes(debouncedLocationFilter.toLowerCase())
      : true;
  });

  const handleNext = () => {
    if (offset + limit < totalCount) {
      setOffset(offset + limit);
    }
  };

  const handlePrev = () => {
    if (offset > 0) {
      setOffset(offset - limit);
    }
  };

  // Remove the handleBuy function's API logic. Instead, just call onNumberSelect with the plain phone number.

  const handleReserve = useCallback(async (phoneNumber) => {
    try {
      const normalizedPhoneNumber = phoneNumber.startsWith("+")
        ? phoneNumber
        : `+${phoneNumber}`;
      const baseUrl =
        "https://prod.toxboxai.com";
      const reserveApiUrl = normalizedPhoneNumber.startsWith("+91")
        ? `${baseUrl}/api/plivo/reserve`
        : `${baseUrl}/api/twilio/reserve`;
      const reserveResponse = await fetch(reserveApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ number: normalizedPhoneNumber }),
      });

      const reserveData = await reserveResponse.json();
      if (!reserveResponse.ok) {
        throw new Error(reserveData.message || "Failed to reserve number");
      }

      alert(`Number ${normalizedPhoneNumber} reserved successfully!`);
    } catch (error) {
      console.error("Error reserving number:", error);
      alert(`Failed to reserve number: ${error.message}`);
    }
  }, []);

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Available Phone Numbers</h2>

      <div className={styles.searchContainer}>
        {/* Country Dropdown */}
        <div className={styles.dropdownContainer}>
          <div className={styles.customSelect} onClick={toggleDropdown}>
            {selectedCountryFlag && (
              <span className={styles.flag}>
                <img
                  src={selectedCountryFlag}
                  alt="flag"
                  className={styles.flagImg}
                />
              </span>
            )}
            <span className={styles.countryName}>{selectedCountryName}</span>
            <span className={styles.arrow}>
              <IoIosArrowDown />
            </span>
          </div>

          {isDropdownOpen && (
            <div className={styles.dropdownMenu}>
              {countries.map((country) => (
                <div
                  key={country.code}
                  className={styles.dropdownItem}
                  onClick={() => handleCountrySelect(country)}
                >
                  <img
                    src={country.flag}
                    alt="flag"
                    className={styles.flagImg}
                  />
                  {country.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Location Filter */}
        <div className={styles.radioGroup}>
          <input
            type="text"
            placeholder="Filter by location"
            value={locationFilter}
            onChange={handleLocationFilterChange}
            className="border border-gray-300 rounded px-3 py-2 w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {loading ? (
        <LoaderSpiner />
      ) : selectedCountry &&
        filteredNumbers.length === 0 &&
        (!assignedNumbers.length || mode !== "inbound") ? (
        <p>
          No numbers available for{" "}
          {countries.find((c) => c.code === selectedCountry)?.name}
          {debouncedLocationFilter
            ? ` with location "${debouncedLocationFilter}"`
            : ""}
          .
        </p>
      ) : selectedCountry ? (
        <>
          {/* Assigned Numbers Table (Inbound Only) */}
          {mode === "inbound" && assignedNumbers.length > 0 && (
            <div className="mt-6">
              <h3 className="text-xl font-bold mb-4 text-gray-800">
                Assigned Numbers
              </h3>
              <table className={styles.table}>
                <thead>
                  <tr className={styles.tr}>
                    <th className={styles.th}>Number</th>
                    <th className={styles.th}>Location</th>
                    <th className={styles.th}>Capabilities</th>
                    <th className={styles.th}>Monthly Fee</th>
                    <th className={styles.th}>Status</th>
                    <th className={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {assignedNumbers.map((num, index) => (
                    <tr key={index} className={styles.tr}>
                      <td className={styles.td}>{`+${num.number}`}</td>
                      <td className={styles.td}>
                        {num.city ? `${num.city}, ` : ""}
                        {num.region}
                      </td>
                      <td className={styles.td}>
                        {[
                          num.voice_enabled ? "Voice" : "",
                          num.sms_enabled ? "SMS" : "",
                          num.mms_enabled ? "MMS" : "",
                        ]
                          .filter(Boolean)
                          .join(", ") || "N/A"}
                      </td>
                      <td className={styles.td}>
                        ${num.monthly_rental_rate || "N/A"}
                      </td>
                      <td className={styles.td}>Assigned</td>
                      <td className={styles.td}>
                        <button
                          className={`${styles.button} opacity-50 cursor-not-allowed`}
                          disabled
                        >
                          Buy
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Available Numbers Table */}
          <div className="mt-6">
            <h3 className="text-xl font-bold mb-4 text-gray-800">
              Available Numbers
            </h3>
            <table className={styles.table}>
              <thead>
                <tr className={styles.tr}>
                  <th className={styles.th}>Number</th>
                  <th className={styles.th}>Location</th>
                  <th className={styles.th}>Capabilities</th>
                  <th className={styles.th}>Monthly Fee</th>
                  <th className={styles.th}>Status</th>
                  <th className={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredNumbers.map((num, index) => (
                  <tr key={index} className={styles.tr}>
                    <td className={styles.td}>{`+${num.number}`}</td>
                    <td className={styles.td}>
                      {num.city ? `${num.city}, ` : ""}
                      {num.region}
                    </td>
                    <td className={styles.td}>
                      {[
                        num.voice_enabled ? "Voice" : "",
                        num.sms_enabled ? "SMS" : "",
                        num.mms_enabled ? "MMS" : "",
                      ]
                        .filter(Boolean)
                        .join(", ") || "N/A"}
                    </td>
                    <td className={styles.td}>
                      ${num.monthly_rental_rate || "N/A"}
                    </td>
                    <td className={styles.td}>Available</td>
                    <td className={styles.td}>
                      <button
                        className={styles.button}
                        onClick={() => onNumberSelect(`+${num.number}`)}
                      // No longer using isBuying, as the parent will handle loading
                      >
                        Buy
                      </button>
                      <button
                        className={`${styles.button} ml-2`}
                        onClick={() => handleReserve(num.number)}
                      >
                        Reserve
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className={styles.pagination}>
              <button
                className={styles.button}
                onClick={handlePrev}
                disabled={offset === 0}
              >
                Prev
              </button>
              <span>
                Showing {offset + 1} - {Math.min(offset + limit, totalCount)} of{" "}
                {totalCount}
              </span>
              <button
                className={styles.button}
                onClick={handleNext}
                disabled={offset + limit >= totalCount}
              >
                Next
              </button>
            </div>
          </div>
        </>
      ) : (
        <p>Please select a country to view available numbers.</p>
      )}
    </div>
  );
};

export default PhoneNumberList;
